import { Box, Card, CardContent, Grid, Stack, Step, StepContent, StepLabel, Stepper, TextField, Typography } from '@mui/material';
import {
    Timeline,
    TimelineItem,
    TimelineSeparator,
    TimelineDot,
    TimelineConnector,
    TimelineContent,
    timelineItemClasses,
} from '@mui/lab';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { SCUIBreadCrumb } from '../../atomic/molecules';
import { SCUIContent } from '../../atomic/organisms';
import { SCUIButton, SCUILoadingLinear } from '../../atomic/atoms';
import { NoResultFoundComponent } from '../CustomerManagement/components/NoResultFoundComponent/NoResultFoundComponent';
import ProductShotHistoryQuery from '../../services/query/productShotHistory/ProductShotHistory.services';
import { downloadExcel } from '../../utils/excelFileHelper';
import TimeHelper from '../../utils/time';
import { PSH } from './productShotHistory.classes';
import { PageUrl } from '../../types/enums/pageUrl-enums';

type ProductSummary = {
    productName: string;
    specialCode: string;
    colorCode: string;
    barcode: number;
    line: string;
    inputTypeId?: number;
    inputTypeName?: string;
    productAcceptanceDate?: string;
    productImageUrl?: string;
    colorName?: string;
};

type Personnel = {
    employeeId: number;
    name: string;
};

type TimelineItemData = {
    historyId: number;
    shotId: number;
    eventAt: string;
    previousStatusName?: string;
    newStatusName: string;
    createdBy: string;
    isCancellation: boolean;
    cancelReason?: string;
    cancelDescription?: string;
    reshotReason?: string;
    personnel: Personnel[];
};

type ShotTimelineGroup = {
    shotId: number;
    items: TimelineItemData[];
};

const parseEventTimestamp = (value?: string): number => {
    if (!value) {
        return 0;
    }

    const timestamp = Date.parse(value);
    return Number.isNaN(timestamp) ? 0 : timestamp;
};

const buildShotGroups = (items: TimelineItemData[]): ShotTimelineGroup[] => {
    const groupMap = new Map<number, TimelineItemData[]>();

    items.forEach((item) => {
        const existing = groupMap.get(item.shotId) ?? [];
        existing.push(item);
        groupMap.set(item.shotId, existing);
    });

    return Array.from(groupMap.entries())
        .map(([shotId, groupItems]) => {
            const sortedItems = [...groupItems].sort(
                (a, b) =>
                    parseEventTimestamp(a.eventAt) - parseEventTimestamp(b.eventAt) ||
                    a.historyId - b.historyId
            );

            return {
                shotId,
                items: sortedItems,
            };
        })
        .sort((a, b) => {
            const aStart = parseEventTimestamp(a.items[0]?.eventAt);
            const bStart = parseEventTimestamp(b.items[0]?.eventAt);

            return aStart - bStart || a.shotId - b.shotId;
        });
};

const getLatestTimelineItem = (items: TimelineItemData[]): TimelineItemData | undefined =>
    items.length > 0 ? items[items.length - 1] : undefined;

type AcceptanceCycle = {
    productId: number;
    cycleIndex: number;
    isArchived: boolean;
    summary: ProductSummary;
    timeline: TimelineItemData[];
};

const TimelineEventList = ({ items }: { items: TimelineItemData[] }) => {
    const { t } = useTranslation();

    return (
        <Timeline
            sx={{
                [`& .${timelineItemClasses.root}:before`]: { flex: 0, padding: 0 },
                mt: 1,
            }}
        >
            {items.map((item) => (
                <TimelineItem key={item.historyId} className={PSH.timelineItem}>
                    <TimelineSeparator>
                        <TimelineDot color={item.isCancellation ? 'error' : 'primary'} />
                        <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent>
                        <Typography variant="body2" color="text.secondary">
                            {TimeHelper.formatDateHourSecond(item.eventAt)} — {item.createdBy}
                        </Typography>
                        <Typography variant="body1">
                            {item.previousStatusName ? (
                                <>
                                    <strong>{item.previousStatusName}</strong> &#10148;{' '}
                                </>
                            ) : null}
                            <strong>{item.newStatusName}</strong>
                        </Typography>
                        {item.reshotReason && (
                            <Typography variant="body2" color="text.secondary">
                                {t('productShotHistory.reshotReason')}: {item.reshotReason}
                            </Typography>
                        )}
                        {item.personnel?.length > 0 && (
                            <Typography variant="body2">
                                {t('productShotHistory.personnel')}:{' '}
                                {item.personnel.map((p) => p.name || p.employeeId).join(', ')}
                            </Typography>
                        )}
                        {item.isCancellation && (item.cancelReason || item.cancelDescription) && (
                            <Box
                                className={PSH.cancellationBox}
                                sx={{
                                    mt: 1,
                                    p: 1.5,
                                    borderRadius: 1,
                                    bgcolor: 'error.light',
                                    color: 'error.contrastText',
                                }}
                            >
                                <Typography variant="subtitle2">{t('productShotHistory.cancellationTitle')}</Typography>
                                {item.cancelReason && (
                                    <Typography variant="body2">
                                        {t('productShotHistory.cancelReason')}: {item.cancelReason}
                                    </Typography>
                                )}
                                {item.cancelDescription && (
                                    <Typography variant="body2">
                                        {t('productShotHistory.cancelDescription')}: {item.cancelDescription}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </TimelineContent>
                </TimelineItem>
            ))}
        </Timeline>
    );
};

const getLocalizedInputType = (
    t: (key: string, options?: { defaultValue?: string }) => string,
    inputTypeId?: number,
    inputTypeName?: string
) => {
    if (inputTypeId == null) {
        return inputTypeName?.trim() || '-';
    }

    return t(`productShotHistory.inputTypes.${inputTypeId}`, {
        defaultValue: inputTypeName?.trim() || '-',
    });
};

export const ProductShotHistory = () => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [summary, setSummary] = useState<ProductSummary | null>(null);
    const [timeline, setTimeline] = useState<TimelineItemData[]>([]);
    const [acceptanceCycles, setAcceptanceCycles] = useState<AcceptanceCycle[]>([]);
    const [expandedShotId, setExpandedShotId] = useState<number | null>(null);

    const shotGroups = useMemo((): ShotTimelineGroup[] => {
        return buildShotGroups(timeline);
    }, [timeline]);

    const mapTimelineItem = (item: any): TimelineItemData => ({
        historyId: item.historyId,
        shotId: item.shotId,
        eventAt: item.eventAt,
        previousStatusName: item.previousStatusName,
        newStatusName: item.newStatusName,
        createdBy: item.createdBy,
        isCancellation: item.isCancellation,
        cancelReason: item.cancelReason,
        cancelDescription: item.cancelDescription,
        reshotReason: item.reshotReason,
        personnel: item.personnel ?? [],
    });

    const mapSummary = (summaryData: any): ProductSummary => ({
        productName: summaryData.productName,
        specialCode: summaryData.specialCode,
        colorCode: summaryData.colorCode,
        barcode: summaryData.barcode,
        line: summaryData.line,
        inputTypeId: summaryData.inputTypeId,
        inputTypeName: summaryData.inputTypeName,
        productAcceptanceDate: summaryData.productAcceptanceDate,
        productImageUrl: summaryData.productImageUrl,
    });

    const handleSearch = async () => {
        const trimmed = searchTerm.trim();
        if (!trimmed) {
            return;
        }

        // Format validation: Barcode (13 digits), Special-Color with dash, or Keycode (11 characters)
        const isBarcode = /^\d{13}$/.test(trimmed);
        const isSpecialColorWithDash = trimmed.includes('-') && trimmed.indexOf('-') > 0 && trimmed.indexOf('-') < trimmed.length - 1;
        const isKeyCode = !trimmed.includes('-') && trimmed.length === 11;

        if (!isBarcode && !isSpecialColorWithDash && !isKeyCode) {
            await Swal.fire({
                icon: 'warning',
                title: t('general.warning'),
                text: t('productShotHistory.invalidSearchFormat', 'Lütfen geçerli bir Barkod (13 hane), Özel-Renk Kodu (Örn: ABC123-456) veya Keycode (11 hane) giriniz.'),
            });
            return;
        }

        try {
            setLoading(true);
            setSearched(true);
            setExpandedShotId(null);
            const response = await ProductShotHistoryQuery.GetProductShotHistory({ searchTerm: trimmed });
            const data = response?.data;

            if (!data?.summary) {
                setSummary(null);
                setTimeline([]);
                setAcceptanceCycles([]);
                return;
            }

            setSummary(mapSummary(data.summary));
            const nextTimeline = (data.timeline ?? []).map(mapTimelineItem);
            setTimeline(nextTimeline);
            setAcceptanceCycles(
                (data.acceptanceCycles ?? []).map((cycle: any) => ({
                    productId: cycle.productId,
                    cycleIndex: cycle.cycleIndex,
                    isArchived: cycle.isArchived,
                    summary: mapSummary(cycle.summary),
                    timeline: (cycle.timeline ?? []).map(mapTimelineItem),
                }))
            );

            const groupedShots = buildShotGroups(nextTimeline);
            let latestShotId: number | null = null;
            let latestTimestamp = -1;

            groupedShots.forEach((group) => {
                const latestItem = getLatestTimelineItem(group.items);
                const timestamp = parseEventTimestamp(latestItem?.eventAt);
                if (timestamp > latestTimestamp) {
                    latestTimestamp = timestamp;
                    latestShotId = group.shotId;
                }
            });

            setExpandedShotId(latestShotId);
        } catch {
            Swal.fire(t('general.error'), t('general.anErrorOccured'), 'error');
            setSummary(null);
            setTimeline([]);
            setAcceptanceCycles([]);
            setExpandedShotId(null);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleShotGroup = (shotId: number) => {
        setExpandedShotId((current) => (current === shotId ? null : shotId));
    };

    const handleExportExcel = () => {
        if (!summary) {
            return;
        }

        const rows: Record<string, string | number>[] = [
            {
                level: t('productShotHistory.excel.levelProduct'),
                productName: summary.productName,
                specialColorCode: `${summary.specialCode}-${summary.colorCode}`,
                barcode: summary.barcode,
                line: summary.line ?? '',
                productAcceptanceDate: summary.productAcceptanceDate
                    ? TimeHelper.formatDateHourSecond(summary.productAcceptanceDate)
                    : '',
                shotId: '',
                eventAt: '',
                previousStatusName: '',
                newStatusName: '',
                createdBy: '',
                cancelReason: '',
                cancelDescription: '',
                reshotReason: '',
                personnel: '',
            },
        ];

        shotGroups.forEach((group) => {
            rows.push({
                level: t('productShotHistory.excel.levelShot'),
                productName: '',
                specialColorCode: '',
                barcode: '',
                line: '',
                productAcceptanceDate: '',
                shotId: group.shotId,
                eventAt: '',
                previousStatusName: '',
                newStatusName: '',
                createdBy: '',
                cancelReason: '',
                cancelDescription: '',
                reshotReason: '',
                personnel: '',
            });

            group.items.forEach((item) => {
                rows.push({
                    level: t('productShotHistory.excel.levelHistory'),
                    productName: '',
                    specialColorCode: '',
                    barcode: '',
                    line: '',
                    productAcceptanceDate: '',
                    shotId: item.shotId,
                    eventAt: TimeHelper.formatDateHourSecond(item.eventAt),
                    previousStatusName: item.previousStatusName ?? '',
                    newStatusName: item.newStatusName,
                    createdBy: item.createdBy ?? '',
                    cancelReason: item.cancelReason ?? '',
                    cancelDescription: item.cancelDescription ?? '',
                    reshotReason: item.reshotReason ?? '',
                    personnel: '',
                });

                item.personnel?.forEach((person) => {
                    rows.push({
                        level: t('productShotHistory.excel.levelPersonnel'),
                        productName: '',
                        specialColorCode: '',
                        barcode: '',
                        line: '',
                        productAcceptanceDate: '',
                        shotId: item.shotId,
                        eventAt: TimeHelper.formatDateHourSecond(item.eventAt),
                        previousStatusName: '',
                        newStatusName: '',
                        createdBy: '',
                        cancelReason: '',
                        cancelDescription: '',
                        reshotReason: '',
                        personnel: person.name ?? String(person.employeeId),
                    });
                });
            });
        });

        downloadExcel({
            excelDownloadData: rows,
            excelDownloadFolderName: 'ProductShotHistory',
            fileNameOverride: `ProductShotHistory-${summary.barcode}.xlsx`,
        });
    };

    const showNoResult = searched && !loading && !summary;

    return (
        <SCUIContent>
            <Grid className={PSH.page}>
                <div className={PSH.breadcrumbSection}>
                    <SCUIBreadCrumb
                        title={t('productShotHistory.pageTitle')}
                        componentLink={PageUrl.studioRoutes.productShotHistory.url}
                        style={{ cursor: 'pointer' }}
                    />
                </div>

                <Grid className={PSH.loadingSection} size={{ xs: 12 }}>
                    {loading && <SCUILoadingLinear display={loading} />}
                </Grid>

                <Grid
                    className={PSH.searchRow}
                    container
                    spacing={2}
                    alignItems="center"
                    sx={{ pt: 2, pb: 2 }}
                >
                    <Grid
                        className={PSH.searchField}
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 4,
                        }}
                    >
                        <TextField
                            label={t('productShotHistory.searchPlaceholder')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSearch();
                                }
                            }}
                            fullWidth
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                    </Grid>
                    <Grid
                        className={PSH.searchActions}
                        size={{ xs: 12, md: 'auto' }}
                        sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}
                    >
                        <SCUIButton
                            text={t('general.search')}
                            type="primary"
                            buttonType="button"
                            sizeType="default"
                            size="m"
                            onClick={handleSearch}
                        />
                        {summary && (
                            <SCUIButton
                                className={PSH.exportButtonWrap}
                                text={t('productShotHistory.exportToExcel')}
                                type="secondary"
                                buttonType="button"
                                sizeType="default"
                                size="m"
                                onClick={handleExportExcel}
                            />
                        )}
                    </Grid>
                </Grid>

                {summary && (
                    <Grid className={PSH.contentSection} size={{ xs: 12 }}>
                        <Card className={PSH.summaryCard} sx={{ mb: 3 }}>
                            <CardContent>
                                <Grid container spacing={2} alignItems="center">
                                    {summary.productImageUrl && (
                                        <Grid className={PSH.summaryImage} size={{ xs: 12, sm: 3, md: 2 }}>
                                            <Box
                                                component="img"
                                                src={summary.productImageUrl}
                                                alt={summary.productName}
                                                sx={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain' }}
                                            />
                                        </Grid>
                                    )}
                                    <Grid
                                        className={PSH.summaryDetails}
                                        size={{ xs: 12, sm: summary.productImageUrl ? 9 : 12 }}
                                    >
                                        <Typography variant="body2">
                                            {t('productShotHistory.productName')}: {summary.productName || '-'}
                                        </Typography>
                                        <Typography variant="body2">
                                            {t('productShotHistory.productCode')}: {summary.specialCode}-
                                            {summary.colorCode} {summary.colorName ? `(${summary.colorName})` : ''}
                                        </Typography>
                                        <Typography variant="body2">
                                            {t('general.barcode')}: {summary.barcode}
                                        </Typography>
                                        <Typography variant="body2">
                                            {t('productShotHistory.acceptanceDate')}:{' '}
                                            {summary.productAcceptanceDate
                                                ? TimeHelper.formatDateHourSecond(summary.productAcceptanceDate)
                                                : '-'}
                                        </Typography>
                                        <Typography variant="body2">
                                            {t('productShotHistory.line')}: {summary.line || '-'}
                                        </Typography>
                                        <Typography variant="body2">
                                            {t('productShotHistory.inputType')}:{' '}
                                            {getLocalizedInputType(t, summary.inputTypeId, summary.inputTypeName)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {acceptanceCycles.length > 1 && (
                            <Box className={PSH.timelineSection} sx={{ mb: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    {t('productShotHistory.acceptanceCyclesTitle')}
                                </Typography>
                                <Stack spacing={2}>
                                    {acceptanceCycles.map((cycle) => {
                                        const cycleShotGroups = buildShotGroups(cycle.timeline);

                                        return (
                                            <Card key={cycle.productId} variant="outlined">
                                                <CardContent>
                                                    <Stack
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        sx={{ mb: 1 }}
                                                    >
                                                        <Typography variant="subtitle1">
                                                            {t('productShotHistory.acceptanceCycleLabel', {
                                                                index: cycle.cycleIndex,
                                                            })}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                px: 1,
                                                                py: 0.25,
                                                                borderRadius: 1,
                                                                bgcolor: cycle.isArchived
                                                                    ? 'grey.200'
                                                                    : 'success.light',
                                                            }}
                                                        >
                                                            {cycle.isArchived
                                                                ? t('productShotHistory.archivedCycle')
                                                                : t('productShotHistory.activeCycle')}
                                                        </Typography>
                                                    </Stack>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {t('productShotHistory.acceptanceDate')}:{' '}
                                                        {cycle.summary.productAcceptanceDate
                                                            ? TimeHelper.formatDateHourSecond(
                                                                  cycle.summary.productAcceptanceDate
                                                              )
                                                            : '-'}
                                                    </Typography>
                                                    {cycleShotGroups.length > 0 ? (
                                                        <Box sx={{ mt: 2 }}>
                                                            {cycleShotGroups.map((group) => (
                                                                <Typography
                                                                    key={group.shotId}
                                                                    variant="body2"
                                                                    sx={{ mb: 0.5 }}
                                                                >
                                                                    {t('productShotHistory.shotGroupLabel')} #
                                                                    {group.shotId} ·{' '}
                                                                    {t('productShotHistory.eventCount', {
                                                                        count: group.items.length,
                                                                    })}
                                                                </Typography>
                                                            ))}
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                            {t('productShotHistory.noTimeline')}
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        )}

                        {shotGroups.length > 0 ? (
                            <Box className={PSH.timelineSection}>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    {t('productShotHistory.timelineTitle')}
                                </Typography>
                                <Stepper
                                    className={PSH.shotStepper}
                                    orientation="vertical"
                                    nonLinear
                                    sx={{ pl: 0 }}
                                >
                                    {shotGroups.map((group) => {
                                        const isExpanded = expandedShotId === group.shotId;
                                        const latestItem = getLatestTimelineItem(group.items);

                                        return (
                                            <Step
                                                key={group.shotId}
                                                expanded={isExpanded}
                                                sx={{ '&:first-of-type': { pl: 0 } }}
                                            >
                                                <StepLabel
                                                    className={PSH.shotStepLabel}
                                                    onClick={() => handleToggleShotGroup(group.shotId)}
                                                    sx={{ cursor: 'pointer' }}
                                                >
                                                    <Box>
                                                        <Typography variant="subtitle1" component="span">
                                                            {t('productShotHistory.shotGroupLabel')} #{group.shotId}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            component="div"
                                                        >
                                                            {t('productShotHistory.eventCount', {
                                                                count: group.items.length,
                                                            })}
                                                            {latestItem
                                                                ? ` · ${latestItem.newStatusName} · ${TimeHelper.formatDateHourSecond(latestItem.eventAt)}`
                                                                : ''}
                                                        </Typography>
                                                    </Box>
                                                </StepLabel>
                                                <StepContent>
                                                    <TimelineEventList items={group.items} />
                                                </StepContent>
                                            </Step>
                                        );
                                    })}
                                </Stepper>
                            </Box>
                        ) : (
                            <NoResultFoundComponent errorText={t('productShotHistory.noTimeline')} />
                        )}
                    </Grid>
                )}

                {showNoResult && (
                    <Grid size={{ xs: 12 }}>
                        <NoResultFoundComponent errorText={t('productShotHistory.noResult')} />
                    </Grid>
                )}
            </Grid>
        </SCUIContent>
    );
};
